import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderPlus,
  MoreHorizontal,
  Plus,
  Play,
  RotateCcw,
  Upload,
  Download,
  Users,
  Loader2,
  Send,
  FileText,
  Pencil,
  PanelRightClose,
  PanelRightOpen,
  ChevronUp,
  Paperclip,
  X,
  History,
  FileUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import api, { getAvailableAIModels } from '../../api';
import {
  tasksApi,
  type ClientNode,
  type ProjectNode,
  type TaskNode,
  type TaskRun,
  type RequiredUpload,
  type SkillAuthoring,
  type TaskFile,
  type SkillVersion,
  type SkillImportWarning,
} from './tasksApi';

const FOLLOW_UP_UPLOAD_KEY = 'follow-up';
const TASK_FILE_ACCEPT = '.pdf,.docx,.doc,.txt,.md,.xlsx,.csv,.png,.jpg,.jpeg';
const SIDEBAR_MIN = 240;
const SIDEBAR_MAX = 480;
const SIDEBAR_DEFAULT = 320;
const SIDEBAR_LS_KEY = 'jarvis.tasks.sidebarWidth';

function clampSidebarWidth(width: number, containerWidth: number) {
  const cap = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.floor(containerWidth * 0.4)));
  return Math.min(cap, Math.max(SIDEBAR_MIN, Math.round(width)));
}

function readStoredSidebarWidth() {
  try {
    const n = Number(window.localStorage.getItem(SIDEBAR_LS_KEY));
    if (Number.isFinite(n) && n >= SIDEBAR_MIN) return Math.min(n, SIDEBAR_MAX);
  } catch {
    /* ignore */
  }
  return SIDEBAR_DEFAULT;
}

function artifactBaseName(name: string) {
  return String(name || '').replace(/\s*\(\d+\)(?=\.[^.]+$)/, '');
}

function versionedDownloadName(baseName: string, version: number, includeVersion = version > 1) {
  if (!includeVersion) return baseName;
  const m = baseName.match(/^(.*)(\.[^.]+)$/);
  if (!m) return `${baseName} (${version})`;
  return `${m[1]} (${version})${m[2]}`;
}

function formatFileTime(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function groupArtifacts(artifacts: TaskFile[]) {
  const groups = new Map<string, TaskFile[]>();
  for (const a of artifacts) {
    const key = artifactBaseName(a.originalName);
    const list = groups.get(key) || [];
    list.push(a);
    groups.set(key, list);
  }
  return Array.from(groups.entries()).map(([baseName, files]) => {
    const sorted = [...files].sort(
      (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
    );
    return {
      baseName,
      versions: sorted.map((file, i) => ({ file, version: i + 1 })),
    };
  });
}

type PathSel = {
  clientId?: number;
  projectId?: number;
  taskId?: number;
  runId?: number;
  edit?: boolean;
};

function parsePath(pathname: string): PathSel {
  const m = pathname.match(
    /\/app\/tasks(?:\/c\/(\d+))?(?:\/p\/(\d+))?(?:\/t\/(\d+))?(?:\/r\/(\d+))?(?:\/(edit))?/
  );
  if (!m) return {};
  return {
    clientId: m[1] ? Number(m[1]) : undefined,
    projectId: m[2] ? Number(m[2]) : undefined,
    taskId: m[3] ? Number(m[3]) : undefined,
    runId: m[4] ? Number(m[4]) : undefined,
    edit: m[5] === 'edit',
  };
}

function buildPath(sel: PathSel) {
  let p = '/app/tasks';
  if (sel.clientId) p += `/c/${sel.clientId}`;
  if (sel.projectId) p += `/p/${sel.projectId}`;
  if (sel.taskId) p += `/t/${sel.taskId}`;
  if (sel.runId) p += `/r/${sel.runId}`;
  if (sel.edit) p += '/edit';
  return p;
}

type MenuState = {
  type: string;
  id: number;
  x: number;
  y: number;
  name?: string;
  code?: string | null;
  deleted?: boolean;
};

function deletedSuffix(node: { deleted?: boolean; purgeAfter?: string | null }) {
  if (!node.deleted) return '';
  if (!node.purgeAfter) return ' — deleted';
  const days = Math.ceil((new Date(node.purgeAfter).getTime() - Date.now()) / 86400000);
  if (!Number.isFinite(days)) return ' — deleted';
  if (days <= 0) return ' — deleted, purging soon';
  return ` — deleted, ${days}d left`;
}

const WORK_BLOCK_RE = /<<<(?:FILE:[^>]+|[A-Z0-9_]+)>>>\s*[\s\S]*?\s*<<<END_(?:FILE|[A-Z0-9_]+)>>>/gi;

function isTimeoutError(err: any) {
  return err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
}

function splitAssistantContent(content: string) {
  const blocks = content.match(WORK_BLOCK_RE) || [];
  if (blocks.length) {
    const visible = content
      .replace(WORK_BLOCK_RE, '')
      .replace(/<<<(?:FILE:[^>]+|[A-Z0-9_]+)>>>/g, '')
      .trim();
    return { visible, work: blocks.join('\n\n').trim() };
  }
  if (/<<<(?:FILE:[^>]+|[A-Z0-9_]+)>>>/.test(content)) {
    return { visible: '', work: content };
  }
  return { visible: content, work: '' };
}

function runDotClass(status?: string) {
  if (status === 'running') return 'bg-blue-500 animate-pulse';
  if (status === 'completed') return 'bg-emerald-500';
  if (status === 'failed') return 'bg-red-500';
  return 'bg-gray-400';
}

const TasksPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sel = parsePath(location.pathname);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [clients, setClients] = useState<ClientNode[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [menu, setMenu] = useState<MenuState | null>(null);

  const [modal, setModal] = useState<
    | { kind: 'project'; clientId: number }
    | { kind: 'rename-project'; projectId: number; name: string; code?: string | null }
    | { kind: 'members'; projectId: number }
    | null
  >(null);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const shellRef = useRef<HTMLDivElement>(null);
  const draggingSidebar = useRef(false);

  const [taskDetail, setTaskDetail] = useState<{
    id: number;
    name: string;
    summary: string;
    requiredUploads: RequiredUpload[];
    isDraft?: boolean;
  } | null>(null);
  const [run, setRun] = useState<TaskRun | null>(null);
  const [canWrite, setCanWrite] = useState(true);
  const [comments, setComments] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [busy, setBusy] = useState(false);

  const runningRef = useRef<Set<number>>(new Set());
  const pollRef = useRef<number | null>(null);
  const selectedRunId = sel.runId;

  useEffect(() => {
    setSidebarWidth(readStoredSidebarWidth());
  }, []);

  useEffect(() => {
    const onResize = () => {
      const box = shellRef.current?.getBoundingClientRect();
      if (!box) return;
      setSidebarWidth((w) => clampSidebarWidth(w, box.width));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const persistSidebarWidth = (width: number) => {
    const box = shellRef.current?.getBoundingClientRect();
    const next = clampSidebarWidth(width, box?.width || 1200);
    setSidebarWidth(next);
    try {
      window.localStorage.setItem(SIDEBAR_LS_KEY, String(next));
    } catch {
      /* ignore */
    }
    return next;
  };

  const onSidebarHandleDown = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingSidebar.current = true;
    const startX = e.clientX;
    const startW = sidebarWidth;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!draggingSidebar.current) return;
      const box = shellRef.current?.getBoundingClientRect();
      setSidebarWidth(clampSidebarWidth(startW + (ev.clientX - startX), box?.width || 1200));
    };
    const onUp = (ev: MouseEvent) => {
      draggingSidebar.current = false;
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      persistSidebarWidth(startW + (ev.clientX - startX));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const loadTree = useCallback(async () => {
    const res = await tasksApi.tree(showHidden, showDeleted);
    setClients(res.data.clients || []);
  }, [showHidden, showDeleted]);

  useEffect(() => {
    tasksApi
      .capability()
      .then((res) => setAllowed(Boolean(res.data.allowed)))
      .catch(() => setAllowed(false));
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadTree().catch(() => toast.error('Failed to load Tasks'));
  }, [allowed, loadTree]);

  useEffect(() => {
    if (sel.clientId) setExpanded((e) => ({ ...e, [`c:${sel.clientId}`]: true }));
    if (sel.projectId) setExpanded((e) => ({ ...e, [`p:${sel.projectId}`]: true }));
    if (sel.taskId) setExpanded((e) => ({ ...e, [`t:${sel.taskId}`]: true }));
  }, [sel.clientId, sel.projectId, sel.taskId]);

  useEffect(() => {
    if (user && sel.edit && !isAdmin) {
      navigate(buildPath({ ...sel, edit: undefined }));
    }
  }, [user, isAdmin, sel.edit, sel.clientId, sel.projectId, sel.taskId, sel.runId, navigate]);

  const refreshRun = useCallback(async (runId: number, _silent = false) => {
    const res = await tasksApi.getRun(runId);
    const next: TaskRun = res.data.run;
    if (selectedRunId === runId) {
      setRun(next);
      setCanWrite(res.data.canWrite !== false);
      if (next.additionalComments) setComments(next.additionalComments);
    }
    if (next.status === 'running') runningRef.current.add(runId);
    else runningRef.current.delete(runId);
    return next;
  }, [selectedRunId]);

  useEffect(() => {
    if (!sel.taskId || sel.runId) {
      if (!sel.taskId) setTaskDetail(null);
    }
    if (sel.taskId && !sel.runId) {
      tasksApi
        .getTask(sel.taskId)
        .then((res) => setTaskDetail(res.data.task))
        .catch(() => setTaskDetail(null));
      setRun(null);
    }
    if (sel.runId) {
      refreshRun(sel.runId).catch(() => toast.error('Could not load run'));
    }
  }, [sel.taskId, sel.runId, refreshRun]);

  useEffect(() => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(() => {
      const ids = Array.from(runningRef.current);
      if (!ids.length) return;
      ids.forEach((id) => {
        refreshRun(id, true).catch(() => {});
      });
      loadTree().catch(() => {});
    }, 2500);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [refreshRun, loadTree]);

  const selectedTask = useMemo(() => {
    for (const c of clients) {
      for (const p of c.projects) {
        const t = p.tasks.find((x) => x.id === sel.taskId);
        if (t) return t;
      }
    }
    return null;
  }, [clients, sel.taskId]);

  const landingTask = (taskDetail && taskDetail.id === sel.taskId) ? taskDetail : selectedTask;

  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menu]);

  const go = (next: PathSel) => navigate(buildPath(next));

  const toggle = (key: string) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  if (allowed === false) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">Page not found</h1>
        <p className="mt-2 text-sm text-gray-500">The page you're looking for doesn't exist or has moved.</p>
      </div>
    );
  }

  if (allowed === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const onHide = async (entityType: string, entityId: number, admin = false) => {
    try {
      if (admin) await tasksApi.adminHide(entityType, entityId);
      else await tasksApi.hide(entityType, entityId);
      await loadTree();
    } catch {
      toast.error('Could not hide item');
    }
    setMenu(null);
  };

  const onUnhide = async (entityType: string, entityId: number, admin = false) => {
    try {
      if (admin) await tasksApi.adminUnhide(entityType, entityId);
      else await tasksApi.unhide(entityType, entityId);
      await loadTree();
    } catch {
      toast.error('Could not unhide item');
    }
    setMenu(null);
  };

  const startRun = async () => {
    if (!sel.taskId || !sel.clientId || !sel.projectId) return;
    try {
      const res = await tasksApi.createRun(sel.taskId);
      const created: TaskRun = res.data.run;
      go({ clientId: sel.clientId, projectId: sel.projectId, taskId: sel.taskId, runId: created.id });
      await loadTree();
    } catch {
      toast.error('Could not create run');
    }
  };

  const execute = async () => {
    if (!run || !canWrite) return;
    setBusy(true);
    try {
      await tasksApi.execute(run.id, comments);
      runningRef.current.add(run.id);
      await refreshRun(run.id);
      toast.success('Task started');
    } catch (err: any) {
      const missing = err?.response?.data?.missing;
      if (missing?.length) toast.error(`Upload required: ${missing.map((m: any) => m.label).join(', ')}`);
      else toast.error(err?.response?.data?.error || 'Run failed to start');
    } finally {
      setBusy(false);
    }
  };

  const sendChat = async (attached: File[] = []) => {
    if (!run || !canWrite) return;
    const names = attached.map((f) => f.name);
    const content = [chatInput.trim(), names.length ? `Attached: ${names.join(', ')}` : '']
      .filter(Boolean)
      .join('\n\n');
    if (!content) return;
    setBusy(true);
    try {
      for (const file of attached) {
        await tasksApi.upload(run.id, file, FOLLOW_UP_UPLOAD_KEY);
      }
      setChatInput('');
      await tasksApi.sendMessage(run.id, content);
      runningRef.current.add(run.id);
      await refreshRun(run.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not send message');
    } finally {
      setBusy(false);
    }
  };

  const uploadForSlot = async (file: File, uploadKey?: string) => {
    if (!run || !canWrite) return;
    try {
      await tasksApi.upload(run.id, file, uploadKey);
      await refreshRun(run.id);
      toast.success(`Uploaded ${file.name}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    }
  };

  const downloadFile = async (fileId: number, filename: string) => {
    if (!run) return;
    try {
      const res = await api.get(tasksApi.downloadUrl(run.id, fileId), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const required = taskDetail?.requiredUploads || selectedTask?.requiredUploads || [];
  const uploads = run?.files.filter((f) => f.kind === 'upload') || [];
  const artifacts = run?.files.filter((f) => f.kind === 'artifact') || [];
  const hasRunOnce = (run?.messages || []).some((m) => m.role === 'assistant') || run?.status === 'completed';
  const missingRequired = required.filter((spec) => {
    if (!spec.required) return false;
    return !uploads.some((f) => f.uploadKey === spec.key || spec.types.includes((f.originalName.split('.').pop() || '').toLowerCase()));
  });

  return (
    <div ref={shellRef} className="flex h-full min-h-[calc(100vh-120px)] bg-white rounded-xl border border-gray-200 overflow-hidden -m-2">
      <aside
        className="shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col relative"
        style={{ width: sidebarWidth }}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Tasks</h1>
            <p className="text-xs text-gray-500">Client → Project → Task → Run</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded border border-gray-200 bg-white"
              onClick={() => setShowHidden((v) => !v)}
            >
              {showHidden ? 'Hide hidden' : 'Show hidden'}
            </button>
            {isAdmin && (
              <button
                className={`text-xs px-2 py-1 rounded border ${showDeleted ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600 hover:text-gray-900'}`}
                onClick={() => setShowDeleted((v) => !v)}
              >
                {showDeleted ? 'Hide deleted' : 'Show deleted'}
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 text-sm" onClick={() => setMenu(null)}>
          {clients.map((client) => (
            <TreeClient
              key={client.id}
              client={client}
              expanded={expanded}
              toggle={toggle}
              sel={sel}
              go={go}
              isAdmin={isAdmin}
              onMenu={setMenu}
              onAddProject={() => setModal({ kind: 'project', clientId: client.id })}
              onAddTask={async (projectId) => {
                try {
                  const res = await tasksApi.createSkillDraft(projectId);
                  await loadTree();
                  go({ clientId: client.id, projectId, taskId: res.data.task.id, edit: true });
                } catch (err: any) {
                  await loadTree().catch(() => {});
                  toast.error(
                    isTimeoutError(err)
                      ? 'The request timed out. Check the sidebar — the draft may already exist.'
                      : (err?.response?.data?.error || 'Could not start skill creation')
                  );
                }
              }}
              onAddRun={async (projectId, taskId) => {
                const res = await tasksApi.createRun(taskId);
                go({ clientId: client.id, projectId, taskId, runId: res.data.run.id });
                await loadTree();
              }}
              onMembers={(projectId) => setModal({ kind: 'members', projectId })}
            />
          ))}
          {clients.length === 0 && (
            <p className="p-3 text-gray-500 text-sm">No clients visible. Admins can create a project under a client tag.</p>
          )}
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize tasks sidebar"
          title="Drag to resize"
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-30 hover:bg-blue-400/70 active:bg-blue-500"
          onMouseDown={onSidebarHandleDown}
          onDoubleClick={() => persistSidebarWidth(SIDEBAR_DEFAULT)}
        />
      </aside>

      <main className="flex-1 min-w-0 flex flex-col bg-white">
        {!sel.taskId && (
          <div className="flex-1 flex items-center justify-center text-gray-500 p-8 text-center">
            <div>
              <Folder className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="font-medium text-gray-700">Select a task to get started</p>
              <p className="text-sm mt-1">Runs keep their own history. You can jump between in-flight runs without losing progress.</p>
            </div>
          </div>
        )}

        {sel.edit && sel.taskId && isAdmin && (
          <SkillAuthorPane
            skillId={sel.taskId}
            onChanged={loadTree}
            onPublished={async () => {
              await loadTree();
              go({ clientId: sel.clientId, projectId: sel.projectId, taskId: sel.taskId });
            }}
          />
        )}

        {sel.taskId && !sel.runId && !sel.edit && !landingTask && (
          <div className="flex-1 p-8 space-y-3">
            <div className="h-8 w-72 bg-gray-100 rounded animate-pulse" />
            <div className="h-4 w-full max-w-xl bg-gray-100 rounded animate-pulse" />
            <div className="h-4 w-96 bg-gray-100 rounded animate-pulse" />
          </div>
        )}

        {sel.taskId && !sel.runId && !sel.edit && landingTask && (
          <TaskLanding
            name={landingTask.name}
            summary={landingTask.summary}
            required={landingTask.requiredUploads || []}
            onNewRun={startRun}
            isAdmin={isAdmin}
            isDraft={Boolean(landingTask.isDraft)}
            onEdit={() => go({ clientId: sel.clientId, projectId: sel.projectId, taskId: sel.taskId, edit: true })}
          />
        )}

        {sel.runId && run && run.id === sel.runId && !sel.edit && (
          <RunPane
            run={run}
            taskName={taskDetail?.name || selectedTask?.name || 'Task'}
            summary={taskDetail?.summary || selectedTask?.summary || ''}
            required={required}
            comments={comments}
            setComments={setComments}
            chatInput={chatInput}
            setChatInput={setChatInput}
            canWrite={canWrite}
            busy={busy || run.status === 'running'}
            missingRequired={missingRequired}
            hasRunOnce={hasRunOnce}
            artifacts={artifacts}
            onUpload={uploadForSlot}
            onExecute={execute}
            onSend={sendChat}
            onDownload={downloadFile}
            onRename={async (name) => {
              await tasksApi.patchRun(run.id, { name });
              await refreshRun(run.id);
              await loadTree();
            }}
          />
        )}
      </main>

      {menu && (
        <>
          <div className="fixed inset-0 z-40" onMouseDown={() => setMenu(null)} />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm w-48"
            style={{ left: menu.x, top: menu.y }}
          >
          <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50" onClick={() => onHide(menu.type, menu.id)}>
            Hide for me
          </button>
          {showHidden && (
            <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50" onClick={() => onUnhide(menu.type, menu.id)}>
              Unhide for me
            </button>
          )}
          {isAdmin && menu.type === 'project' && (
            <button
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
              onClick={() => {
                setModal({ kind: 'rename-project', projectId: menu.id, name: menu.name || '', code: menu.code });
                setMenu(null);
              }}
            >
              Rename
            </button>
          )}
          {isAdmin && menu.type === 'task' && (
            <button
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
              onClick={() => {
                go({ clientId: sel.clientId, projectId: sel.projectId, taskId: menu.id, edit: true });
                setMenu(null);
              }}
            >
              Edit skill
            </button>
          )}
          {isAdmin && (
            <>
              <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50" onClick={() => onHide(menu.type, menu.id, true)}>
                Hide for everyone
              </button>
              <button className="w-full text-left px-3 py-1.5 hover:bg-gray-50" onClick={() => onUnhide(menu.type, menu.id, true)}>
                Unhide for everyone
              </button>
              {(menu.type === 'project' || menu.type === 'task') && !menu.deleted && (
                <button
                  className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-700"
                  onClick={async () => {
                    if (menu.type === 'project') await tasksApi.deleteProject(menu.id);
                    else await tasksApi.deleteTask(menu.id);
                    setMenu(null);
                    await loadTree();
                    go({ clientId: sel.clientId });
                  }}
                >
                  Delete (30-day recover)
                </button>
              )}
              {(menu.type === 'project' || menu.type === 'task') && menu.deleted && (
                <button
                  className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 text-emerald-700"
                  onClick={async () => {
                    try {
                      if (menu.type === 'project') await tasksApi.restoreProject(menu.id);
                      else await tasksApi.restoreTask(menu.id);
                      toast.success('Restored');
                    } catch {
                      toast.error('Could not restore');
                    }
                    setMenu(null);
                    await loadTree();
                  }}
                >
                  Restore
                </button>
              )}
            </>
          )}
        </div>
        </>
      )}

      {modal?.kind === 'project' && (
        <NameModal
          title="New project"
          placeholder="Project name"
          extraPlaceholder="Code (optional)"
          confirmLabel="Create"
          onClose={() => setModal(null)}
          onSave={async (name, code) => {
            await tasksApi.createProject({ clientTagId: modal.clientId, name, code });
            setModal(null);
            await loadTree();
          }}
        />
      )}
      {modal?.kind === 'rename-project' && (
        <NameModal
          title="Rename project"
          placeholder="Project name"
          extraPlaceholder="Code (optional)"
          initialName={modal.name}
          initialExtra={modal.code || ''}
          confirmLabel="Save"
          onClose={() => setModal(null)}
          onSave={async (name, code) => {
            await tasksApi.patchProject(modal.projectId, { name, code });
            setModal(null);
            await loadTree();
          }}
        />
      )}
      {modal?.kind === 'members' && (
        <MembersModal projectId={modal.projectId} isAdmin={isAdmin} currentUserId={user?.id} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

const TreeClient: React.FC<{
  client: ClientNode;
  expanded: Record<string, boolean>;
  toggle: (k: string) => void;
  sel: PathSel;
  go: (s: PathSel) => void;
  isAdmin: boolean;
  onMenu: (m: MenuState) => void;
  onAddProject: () => void;
  onAddTask: (projectId: number) => void;
  onAddRun: (projectId: number, taskId: number) => void;
  onMembers: (projectId: number) => void;
}> = ({
  client,
  expanded,
  toggle,
  sel,
  go,
  isAdmin,
  onMenu,
  onAddProject,
  onAddTask,
  onAddRun,
  onMembers,
}) => {
  const open = expanded[`c:${client.id}`];
  return (
    <div>
      <Row
        label={client.name}
        depth={0}
        open={open}
        active={sel.clientId === client.id && !sel.projectId}
        dimmed={client.userHidden || client.adminHidden}
        onClick={() => {
          toggle(`c:${client.id}`);
          go({ clientId: client.id });
        }}
        onMenu={(e) => onMenu({ type: 'client', id: client.id, x: e.clientX, y: e.clientY })}
        plus={isAdmin ? onAddProject : undefined}
        plusTitle="New project"
      />
      {open &&
        client.projects.map((project) => (
          <TreeProject
            key={project.id}
            clientId={client.id}
            project={project}
            expanded={expanded}
            toggle={toggle}
            sel={sel}
            go={go}
            isAdmin={isAdmin}
            onMenu={onMenu}
            onAddTask={() => onAddTask(project.id)}
            onAddRun={onAddRun}
            onMembers={() => onMembers(project.id)}
          />
        ))}
    </div>
  );
};

const TreeProject: React.FC<{
  clientId: number;
  project: ProjectNode;
  expanded: Record<string, boolean>;
  toggle: (k: string) => void;
  sel: PathSel;
  go: (s: PathSel) => void;
  isAdmin: boolean;
  onMenu: (m: MenuState) => void;
  onAddTask: () => void;
  onAddRun: (projectId: number, taskId: number) => void;
  onMembers: () => void;
}> = ({ clientId, project, expanded, toggle, sel, go, isAdmin, onMenu, onAddTask, onAddRun, onMembers }) => {
  const open = expanded[`p:${project.id}`];
  return (
    <div>
      <Row
        label={`${project.code ? `${project.name} (${project.code})` : project.name}${deletedSuffix(project)}`}
        depth={1}
        open={open}
        active={sel.projectId === project.id && !sel.taskId}
        dimmed={project.userHidden || project.adminHidden || project.deleted}
        onClick={() => {
          toggle(`p:${project.id}`);
          go({ clientId, projectId: project.id });
        }}
        onMenu={(e) => onMenu({ type: 'project', id: project.id, x: e.clientX, y: e.clientY, name: project.name, code: project.code, deleted: project.deleted })}
        plus={isAdmin ? onAddTask : undefined}
        plusTitle="New task"
        extra={
          <button title="Add collaborator" className="p-0.5 text-gray-400 hover:text-blue-600" onClick={(e) => { e.stopPropagation(); onMembers(); }}>
            <Users className="w-3.5 h-3.5" />
          </button>
        }
      />
      {open &&
        project.tasks.map((task) => (
          <TreeTask
            key={task.id}
            clientId={clientId}
            projectId={project.id}
            task={task}
            expanded={expanded}
            toggle={toggle}
            sel={sel}
            go={go}
            onMenu={onMenu}
            onAddRun={() => onAddRun(project.id, task.id)}
          />
        ))}
    </div>
  );
};

const TreeTask: React.FC<{
  clientId: number;
  projectId: number;
  task: TaskNode;
  expanded: Record<string, boolean>;
  toggle: (k: string) => void;
  sel: PathSel;
  go: (s: PathSel) => void;
  onMenu: (m: MenuState) => void;
  onAddRun: () => void;
}> = ({ clientId, projectId, task, expanded, toggle, sel, go, onMenu, onAddRun }) => {
  const open = expanded[`t:${task.id}`];
  return (
    <div>
      <Row
        label={`${task.name}${task.isDraft ? ' · draft' : ''}${deletedSuffix(task)}`}
        depth={2}
        open={open}
        active={sel.taskId === task.id && !sel.runId}
        dimmed={task.userHidden || task.adminHidden || task.deleted}
        onClick={() => {
          toggle(`t:${task.id}`);
          go({
            clientId,
            projectId,
            taskId: task.id,
            edit: task.isDraft ? true : undefined,
          });
        }}
        onMenu={(e) => onMenu({ type: 'task', id: task.id, x: e.clientX, y: e.clientY, deleted: task.deleted })}
        plus={task.isDraft ? undefined : onAddRun}
        plusTitle="New run"
      />
      {open &&
        task.runs.map((run) => (
          <Row
            key={run.id}
            label={`${run.name}${run.userName ? ` · ${run.userName}` : ''}`}
            depth={3}
            kind="run"
            status={run.status}
            active={sel.runId === run.id}
            onClick={() => go({ clientId, projectId, taskId: task.id, runId: run.id })}
          />
        ))}
    </div>
  );
};

const Row: React.FC<{
  label: string;
  depth: number;
  open?: boolean;
  active?: boolean;
  dimmed?: boolean;
  kind?: 'folder' | 'run';
  status?: string;
  onClick: () => void;
  onMenu?: (e: React.MouseEvent) => void;
  plus?: () => void;
  plusTitle?: string;
  extra?: React.ReactNode;
}> = ({ label, depth, open, active, dimmed, kind = 'folder', status, onClick, onMenu, plus, plusTitle, extra }) => (
  <div
    className={`group flex items-center gap-1 rounded-md px-1 py-1 cursor-pointer ${
      active ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-200/70 text-gray-800'
    } ${dimmed ? 'opacity-50' : ''}`}
    style={{ paddingLeft: 8 + depth * 14 }}
    onClick={onClick}
    onContextMenu={(e) => {
      if (!onMenu) return;
      e.preventDefault();
      onMenu(e);
    }}
  >
    {depth < 3 ? (
      open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />
    ) : (
      <span className="w-3.5 shrink-0" />
    )}
    {kind === 'run' ? (
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${runDotClass(status)}`}
        title={status || 'run'}
      />
    ) : (
      <Folder className="w-3.5 h-3.5 shrink-0 text-gray-500" />
    )}
    <span className="min-w-0 flex-1 truncate">{label}</span>
    <span
      className="shrink-0 relative z-10 flex items-center gap-0.5"
      onClick={(e) => e.stopPropagation()}
    >
      {extra}
      {plus && (
        <button
          title={plusTitle}
          className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-blue-700"
          onClick={(e) => {
            e.stopPropagation();
            plus();
          }}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
      {onMenu && (
        <button
          className="p-1 text-gray-500 hover:text-gray-800"
          title="More"
          onClick={(e) => {
            e.stopPropagation();
            onMenu(e);
          }}
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      )}
    </span>
  </div>
);

const TaskLanding: React.FC<{
  name: string;
  summary: string;
  required: RequiredUpload[];
  onNewRun: () => void;
  isAdmin: boolean;
  isDraft?: boolean;
  onEdit?: () => void;
}> = ({ name, summary, required, onNewRun, isAdmin, isDraft, onEdit }) => (
  <div className="flex-1 overflow-y-auto p-8 max-w-3xl">
    <h2 className="text-2xl font-semibold text-gray-900">
      {isDraft ? `Draft: ${name}` : `Welcome to ${name}`}
    </h2>
    <p className="mt-3 text-gray-700 whitespace-pre-wrap">
      {summary || (isDraft ? 'This skill is not published yet. Finish authoring, then publish it so the project can run it.' : '')}
    </p>
    {required.length > 0 && (
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Documents this task needs</h3>
        <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
          {required.map((r) => (
            <li key={r.key}>
              {r.label} ({r.types.join(', ').toUpperCase()})
              {r.required ? ' — required' : ' — optional'}
            </li>
          ))}
        </ul>
      </div>
    )}
    <div className="mt-8 flex gap-2">
      {isAdmin && onEdit && (
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-800 font-medium hover:bg-gray-50"
        >
          <Pencil className="w-4 h-4" /> {isDraft ? 'Continue editing' : 'Edit skill'}
        </button>
      )}
      {!isDraft && (
        <button
          onClick={onNewRun}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
        >
          <FolderPlus className="w-4 h-4" /> New Run
        </button>
      )}
    </div>
  </div>
);

const ChatMessage: React.FC<{ role: string; content: string }> = ({ role, content }) => {
  if (role !== 'assistant') {
    return (
      <div className="rounded-lg p-3 text-sm whitespace-pre-wrap bg-blue-50 ml-8">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{role}</div>
        {content}
      </div>
    );
  }
  const { visible, work } = splitAssistantContent(content);
  return (
    <div className="rounded-lg p-3 text-sm bg-gray-100 mr-8 space-y-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">assistant</div>
      {work ? <WorkTrail work={work} /> : null}
      {visible ? (
        <div className="whitespace-pre-wrap">{visible}</div>
      ) : work ? (
        <p className="text-gray-600">Finished. Files are in the panel on the right.</p>
      ) : (
        <div className="whitespace-pre-wrap">{content}</div>
      )}
    </div>
  );
};

const WorkTrail: React.FC<{ work: string }> = ({ work }) => {
  const [open, setOpen] = useState(false);
  const blocks = (work.match(/<<<(?:FILE:[^>]+|[A-Z0-9_]+)>>>/g) || []).filter((t) => !t.startsWith('<<<END_'));
  const label = blocks.length
    ? `Work · ${blocks.length} ${blocks.length === 1 ? 'block' : 'blocks'}`
    : 'Work';
  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span className="flex-1 text-left">{label}</span>
        <ChevronUp className={`w-3 h-3 text-gray-400 ${open ? '' : 'rotate-180'}`} />
      </button>
      {open && (
        <pre className="px-3 pb-3 text-[11px] leading-relaxed text-gray-500 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto border-t border-gray-100 pt-2">
          {work}
        </pre>
      )}
    </div>
  );
};

const FilesRail: React.FC<{
  artifacts: TaskFile[];
  onDownload: (id: number, name: string) => void;
}> = ({ artifacts, onDownload }) => {
  const [openPrev, setOpenPrev] = useState<Record<string, boolean>>({});
  const groups = groupArtifacts(artifacts);
  return (
    <aside className="w-64 shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col min-h-0">
      <div className="px-3 py-2 border-b border-gray-200">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Files</h3>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {groups.length === 0 && (
          <p className="text-xs text-gray-500 px-1 py-2">Artifacts from this run will appear here.</p>
        )}
        {groups.map((group) => {
          const latest = group.versions[group.versions.length - 1];
          const older = group.versions.slice(0, -1).reverse();
          const showVersion = group.versions.length > 1;
          const downloadName = versionedDownloadName(group.baseName, latest.version, showVersion);
          return (
            <div key={group.baseName} className="rounded-md">
              <button
                className="w-full flex items-start gap-2 text-left text-sm text-gray-800 hover:bg-white rounded-md px-2 py-1.5"
                onClick={() => onDownload(latest.file.id, downloadName)}
              >
                <Download className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
                <span className="min-w-0">
                  <span className="break-all">
                    {group.baseName}
                    {showVersion ? ` (${latest.version})` : ''}
                  </span>
                  {latest.file.createdAt && (
                    <span className="block text-[11px] text-gray-500">{formatFileTime(latest.file.createdAt)}</span>
                  )}
                </span>
              </button>
              {older.length > 0 && (
                <div className="pl-7 pr-1 pb-1">
                  <button
                    type="button"
                    className="text-[11px] text-gray-500 hover:text-gray-800"
                    onClick={() => setOpenPrev((s) => ({ ...s, [group.baseName]: !s[group.baseName] }))}
                  >
                    {openPrev[group.baseName] ? 'Hide' : 'Previous'} versions ({older.length})
                  </button>
                  {openPrev[group.baseName] && (
                    <ul className="mt-1 space-y-1">
                      {older.map((v) => (
                        <li key={v.file.id}>
                          <button
                            className="w-full text-left text-[11px] text-gray-600 hover:text-blue-700"
                            onClick={() => onDownload(v.file.id, versionedDownloadName(group.baseName, v.version, true))}
                          >
                            ({v.version}) {formatFileTime(v.file.createdAt)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

const RunPane: React.FC<{
  run: TaskRun;
  taskName: string;
  summary: string;
  required: RequiredUpload[];
  comments: string;
  setComments: (v: string) => void;
  chatInput: string;
  setChatInput: (v: string) => void;
  canWrite: boolean;
  busy: boolean;
  missingRequired: RequiredUpload[];
  hasRunOnce: boolean;
  artifacts: TaskFile[];
  onUpload: (file: File, key?: string) => void;
  onExecute: () => void;
  onSend: (files?: File[]) => void;
  onDownload: (id: number, name: string) => void;
  onRename: (name: string) => void;
}> = ({
  run,
  taskName,
  summary,
  required,
  comments,
  setComments,
  chatInput,
  setChatInput,
  canWrite,
  busy,
  missingRequired,
  hasRunOnce,
  artifacts,
  onUpload,
  onExecute,
  onSend,
  onDownload,
  onRename,
}) => {
  const [editingName, setEditingName] = useState(run.name);
  const [filesOpen, setFilesOpen] = useState(true);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const followUpFileRef = useRef<HTMLInputElement>(null);
  useEffect(() => setEditingName(run.name), [run.name]);

  const sendFollowUp = () => {
    if (!canWrite || busy) return;
    if (!chatInput.trim() && pendingFiles.length === 0) return;
    const files = pendingFiles;
    setPendingFiles([]);
    onSend(files);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <input
            className="text-xl font-semibold text-gray-900 w-full bg-transparent outline-none"
            value={editingName}
            disabled={!canWrite}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={() => {
              if (editingName.trim() && editingName !== run.name) onRename(editingName.trim());
            }}
          />
          <p className="text-sm text-gray-600 mt-1">Welcome to {taskName}</p>
          {run.staleSkillVersion && (
            <p className="text-xs text-amber-700 mt-1">This skill was updated. The next Rerun will use the new version.</p>
          )}
        </div>
        <button
          className="shrink-0 p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
          title={filesOpen ? 'Hide files' : 'Show files'}
          onClick={() => setFilesOpen((v) => !v)}
        >
          {filesOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
        </button>
      </div>
      <div className="flex-1 min-h-0 flex">
      <div className="flex-1 min-w-0 overflow-y-auto px-6 py-4 space-y-4">
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{summary}</p>
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Upload Documents</h3>
          <div className="space-y-2">
            {required.map((slot) => {
              const existing = run.files.find((f) => f.kind === 'upload' && f.uploadKey === slot.key);
              return (
                <label key={slot.key} className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg p-3 text-sm cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4 text-gray-500" />
                  <span className="flex-1">
                    {slot.label}
                    {slot.required && <span className="text-red-600"> *</span>}
                    <span className="block text-xs text-gray-500">{existing ? existing.originalName : slot.types.join(', ').toUpperCase()}</span>
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    disabled={!canWrite}
                    accept={slot.types.map((t) => `.${t}`).join(',')}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onUpload(file, slot.key);
                      e.target.value = '';
                    }}
                  />
                </label>
              );
            })}
            <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg p-3 text-sm cursor-pointer hover:bg-gray-50">
              <Paperclip className="w-4 h-4 text-gray-500" />
              <span className="flex-1">
                {required.length ? 'Additional files' : 'Attach files'}
                <span className="block text-xs text-gray-500">
                  {required.length
                    ? 'Optional extras sent with this run'
                    : 'PDF, Word, Excel, images, or markdown'}
                </span>
              </span>
              <input
                type="file"
                multiple
                className="hidden"
                disabled={!canWrite}
                accept={TASK_FILE_ACCEPT}
                onChange={(e) => {
                  const next = Array.from(e.target.files || []);
                  next.forEach((file) => onUpload(file));
                  e.target.value = '';
                }}
              />
            </label>
            {run.files
              .filter((f) => {
                if (f.kind !== 'upload') return false;
                if (!f.uploadKey) return true;
                return !required.some((slot) => slot.key === f.uploadKey);
              })
              .map((f) => (
                <div key={f.id} className="text-xs text-gray-600 px-1">
                  {f.originalName}
                </div>
              ))}
          </div>
        </div>
        {(run.messages || []).map((m) => (
          <ChatMessage key={m.id} role={m.role} content={m.content} />
        ))}
        {run.status === 'running' && (
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="w-4 h-4 animate-spin" /> Running…
          </div>
        )}
      </div>
      {filesOpen && (
        <FilesRail artifacts={artifacts} onDownload={onDownload} />
      )}
      </div>
      <div className="border-t border-gray-200 p-4 space-y-2">
        {!canWrite && <p className="text-xs text-gray-500">Read-only. You cannot add to another user's run.</p>}
        <textarea
          className="w-full border border-gray-300 rounded-lg p-2 text-sm"
          rows={2}
          placeholder="Additional comments (optional)"
          disabled={!canWrite || busy}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
        <div className="flex gap-2">
          <button
            disabled={!canWrite || busy || missingRequired.length > 0}
            onClick={onExecute}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-40"
          >
            {hasRunOnce ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {hasRunOnce ? 'Rerun Task' : 'Run Task'}
          </button>
          {missingRequired.length > 0 && (
            <span className="text-xs text-red-600 self-center">Upload required documents first</span>
          )}
        </div>
        {hasRunOnce && (
          <div className="space-y-2">
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {pendingFiles.map((file, i) => (
                  <span
                    key={`${file.name}-${i}`}
                    className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 text-gray-700"
                  >
                    <span className="max-w-[180px] truncate">{file.name}</span>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-800"
                      onClick={() => setPendingFiles((list) => list.filter((_, idx) => idx !== i))}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center">
              <input
                ref={followUpFileRef}
                type="file"
                multiple
                className="hidden"
                accept={TASK_FILE_ACCEPT}
                disabled={!canWrite || busy}
                onChange={(e) => {
                  const next = Array.from(e.target.files || []);
                  if (next.length) setPendingFiles((list) => [...list, ...next]);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                title="Attach files to this follow-up"
                disabled={!canWrite || busy}
                className="shrink-0 p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                onClick={() => followUpFileRef.current?.click()}
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <input
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Follow-up in this run…"
                disabled={!canWrite || busy}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendFollowUp();
                  }
                }}
              />
              <button
                disabled={!canWrite || busy || (!chatInput.trim() && pendingFiles.length === 0)}
                onClick={sendFollowUp}
                className="shrink-0 px-3 py-2 rounded-lg bg-gray-900 text-white disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SkillAuthorPane: React.FC<{
  skillId: number;
  onChanged: () => Promise<void> | void;
  onPublished: () => Promise<void> | void;
}> = ({ skillId, onChanged, onPublished }) => {
  const [authoring, setAuthoring] = useState<SkillAuthoring | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [models, setModels] = useState<{ id: string; name: string }[]>([]);
  const [orgDefault, setOrgDefault] = useState('claude-4-sonnet');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<SkillVersion[]>([]);
  const [previewVersion, setPreviewVersion] = useState<SkillVersion | null>(null);
  const [editingName, setEditingName] = useState('');
  const [holdDrafting, setHoldDrafting] = useState(false);
  const [importWarnings, setImportWarnings] = useState<SkillImportWarning[]>([]);
  const importInputRef = useRef<HTMLInputElement>(null);
  const prevBusyRef = useRef(false);
  const holdDraftingTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    const res = await tasksApi.getAuthoring(skillId);
    setAuthoring(res.data.task);
  }, [skillId]);

  const loadVersions = useCallback(async () => {
    try {
      const res = await tasksApi.skillVersions(skillId);
      setVersions(res.data.versions || []);
    } catch {
      setVersions([]);
    }
  }, [skillId]);

  useEffect(() => {
    load().catch(() => toast.error('Could not load skill authoring'));
    loadVersions().catch(() => {});
    setPreviewVersion(null);
    setImportWarnings([]);
  }, [load, loadVersions]);

  useEffect(() => {
    setEditingName(authoring?.name || '');
  }, [authoring?.name]);

  useEffect(() => {
    getAvailableAIModels()
      .then((res) => {
        const list = (res.models || []).filter((m) => m.available !== false);
        setModels(list.map((m) => ({ id: m.id, name: m.name })));
        if (res.default) setOrgDefault(res.default);
      })
      .catch(() => {});
  }, []);

  const drafting = busy || holdDrafting || Boolean(authoring?.authoringBusy);

  useEffect(() => () => {
    if (holdDraftingTimer.current) window.clearTimeout(holdDraftingTimer.current);
  }, []);

  useEffect(() => {
    if (!authoring?.authoringBusy) return;
    const id = window.setInterval(() => {
      load().catch(() => {});
    }, 2000);
    return () => window.clearInterval(id);
  }, [authoring?.authoringBusy, load]);

  useEffect(() => {
    const nowBusy = Boolean(authoring?.authoringBusy) || holdDrafting;
    if (prevBusyRef.current && !nowBusy) {
      void onChanged();
      const last = authoring?.messages?.[authoring.messages.length - 1];
      if (last?.role === 'assistant' && String(last.content).startsWith('Authoring failed:')) {
        const text = String(last.content);
        toast.error(
          text.length > 160 || /authentication method|apiKey|not configured/i.test(text)
            ? 'Authoring failed. See the chat for details.'
            : text
        );
      } else if (authoring?.draftSkillMd) {
        toast.success('Draft updated');
      }
    }
    prevBusyRef.current = nowBusy;
  }, [authoring?.authoringBusy, authoring?.draftSkillMd, authoring?.messages, holdDrafting, onChanged]);

  const send = async () => {
    if (!input.trim() || drafting) return;
    const content = input.trim();
    setInput('');
    setBusy(true);
    setHoldDrafting(true);
    if (holdDraftingTimer.current) window.clearTimeout(holdDraftingTimer.current);
    holdDraftingTimer.current = window.setTimeout(() => setHoldDrafting(false), 1600);
    try {
      const res = await tasksApi.authorMessage(skillId, content, authoring?.modelId || undefined);
      const task = res.data.task;
      if (res.data.started || task?.authoringBusy) {
        setAuthoring(task ? { ...task, authoringBusy: true } : task);
        return;
      }
      setAuthoring(task);
      await onChanged();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        if (err.response.data?.task) setAuthoring(err.response.data.task);
        toast.error('Already drafting — wait for the current turn to finish.');
      } else if (isTimeoutError(err)) {
        toast.error('The request timed out, but the skill is still drafting. The draft will appear when it finishes.');
        load().catch(() => {});
      } else {
        toast.error(err?.response?.data?.error || 'Authoring failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!authoring?.draftSkillMd) {
      toast.error('Chat with the skill or import a SKILL.md first so there is a draft to publish');
      return;
    }
    setBusy(true);
    try {
      const res = await tasksApi.publishSkill(skillId);
      const inflight = res.data.inflight || {};
      if (inflight.aborted || inflight.markedStale) {
        toast.success('Published. In-flight runs will finish the old snapshot or stop if they had not started generating.');
      } else {
        toast.success('Skill published');
      }
      setImportWarnings([]);
      await onPublished();
      await loadVersions();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Publish failed');
    } finally {
      setBusy(false);
    }
  };

  const restoreVersion = async (v: SkillVersion) => {
    const warn = authoring?.draftSkillMd
      ? `Replace the current draft with version ${v.version}? Publish again to make it live.`
      : `Load version ${v.version} into the draft?`;
    if (!window.confirm(warn)) return;
    setBusy(true);
    setHistoryOpen(false);
    try {
      const res = await tasksApi.restoreSkillVersion(skillId, v.id);
      setAuthoring(res.data.task);
      setPreviewVersion(null);
      await onChanged();
      toast.success(`Version ${v.version} loaded into the draft`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not restore version');
    } finally {
      setBusy(false);
    }
  };

  const previewHistoryVersion = async (v: SkillVersion) => {
    try {
      const res = await tasksApi.getSkillVersion(skillId, v.id);
      setPreviewVersion(res.data.version);
      setHistoryOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not load version');
    }
  };

  const saveName = async () => {
    const next = editingName.trim();
    if (!next || next === authoring?.name) {
      setEditingName(authoring?.name || '');
      return;
    }
    try {
      const res = await tasksApi.patchSkill(skillId, { name: next });
      setAuthoring(res.data.task);
      await onChanged();
    } catch {
      toast.error('Could not rename skill');
      setEditingName(authoring?.name || '');
    }
  };

  const importSkillFile = async (file: File) => {
    const warn = authoring?.draftSkillMd
      ? `Replace the current draft with ${file.name}? Previous published versions stay recoverable.`
      : null;
    if (warn && !window.confirm(warn)) return;
    setBusy(true);
    try {
      const res = await tasksApi.importSkill(skillId, file);
      setAuthoring(res.data.task);
      setImportWarnings(res.data.warnings || []);
      setPreviewVersion(null);
      await onChanged();
      const count = (res.data.warnings || []).length;
      toast.success(count ? `Imported ${file.name} (${count} warning${count === 1 ? '' : 's'})` : `Imported ${file.name}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const welcome = authoring?.isDraft
    ? 'Import a SKILL.md or zip, or describe the skill you want to create.'
    : 'What would you like to change? Publishing saves a new version.';

  if (!authoring) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-3">
          <FileText className="w-4 h-4 text-gray-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Edit skill</h2>
            <p className="text-xs text-gray-500">Loading authoring session…</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-3">
        <FileText className="w-4 h-4 text-gray-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <input
            className="text-lg font-semibold text-gray-900 w-full bg-transparent outline-none truncate"
            value={editingName}
            disabled={drafting}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={saveName}
          />
          <p className="text-xs text-gray-500">{authoring?.isDraft ? 'Draft — not runnable until published' : 'Editing published skill'}</p>
        </div>
        <label className="text-xs text-gray-600 flex items-center gap-2">
          Model
          <select
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
            value={authoring?.modelId || ''}
            disabled={drafting || !authoring}
            onChange={async (e) => {
              const modelId = e.target.value || null;
              try {
                const res = await tasksApi.patchSkill(skillId, { modelId });
                setAuthoring(res.data.task);
              } catch {
                toast.error('Could not save model');
              }
            }}
          >
            <option value="">Org default ({orgDefault})</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>
        {versions.length > 0 && (
          <div className="relative">
            <button
              type="button"
              disabled={drafting}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1.5"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              <History className="w-3.5 h-3.5" />
              History
            </button>
            {historyOpen && (
              <>
                <div className="fixed inset-0 z-30" onMouseDown={() => setHistoryOpen(false)} />
                <div className="absolute right-0 mt-1 z-40 w-72 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-72 overflow-y-auto">
                  <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-500">Publish history</div>
                  <p className="px-3 pb-1 text-[11px] text-gray-500">Click a version to preview its SKILL.md</p>
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className="px-3 py-2 border-t border-gray-100 flex items-start gap-2 cursor-pointer hover:bg-gray-50"
                      onClick={() => previewHistoryVersion(v)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900">
                          Version {v.version}
                          {v.isCurrent ? <span className="ml-1 text-[11px] text-blue-700">current</span> : null}
                        </div>
                        <div className="text-[11px] text-gray-500">{formatFileTime(v.createdAt)}</div>
                      </div>
                      <button
                        type="button"
                        disabled={drafting}
                        className="shrink-0 text-xs text-blue-700 hover:underline disabled:opacity-40"
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreVersion(v);
                        }}
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <button
          disabled={drafting || !authoring?.draftSkillMd}
          className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40"
          onClick={publish}
        >
          Publish
        </button>
      </div>
      <div className="flex-1 min-h-0 grid grid-cols-2">
        <div className="flex flex-col min-h-0 border-r border-gray-200">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            <p className="text-sm text-gray-700">{welcome}</p>
            <details className="text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-2 bg-white">
              <summary className="cursor-pointer font-medium text-gray-700">What this skill can produce</summary>
              <div className="mt-2 space-y-1 text-gray-600">
                <p>Runs convert delimited text into Word and Excel. Layout is not pixel-perfect.</p>
                <p>There is no image embedding in Office files, no screenshots, and no native Office generation.</p>
                <p>Instruct the skill to emit files with <code className="font-mono">{'<<<FILE:name.ext>>>'}</code> … <code className="font-mono">{'<<<END_FILE>>>'}</code>.</p>
              </div>
            </details>
            <p className="text-xs text-gray-500">
              Import a SKILL.md or a skill zip to replace this draft, or upload context documents and describe the skill. Context files are distilled into the skill and are not re-sent on later runs. Zip references and assets are kept and sent on every run.
            </p>
            {importWarnings.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
                {importWarnings.map((w) => (
                  <p key={w.code}>{w.message}</p>
                ))}
              </div>
            )}
            {(authoring?.packageFiles || []).length > 0 && (
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Skill package files</div>
                {(authoring?.packageFiles || []).map((f) => (
                  <div key={f.id} className="text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-2">
                    {f.path}
                    {f.kind === 'script' ? <span className="ml-1 text-amber-700">(stored, not executed)</span> : null}
                  </div>
                ))}
              </div>
            )}
            {(authoring?.files || []).length > 0 && (
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Context files (distilled, not re-sent)</div>
                {(authoring?.files || []).map((f) => (
                  <div key={f.id} className="text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-2">
                    {f.originalName}
                  </div>
                ))}
              </div>
            )}
            {(authoring?.messages || []).map((m) => (
              <div
                key={m.id}
                className={`rounded-lg p-3 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-blue-50 ml-8' : 'bg-gray-100 mr-8'}`}
              >
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{m.role}</div>
                {m.role === 'assistant'
                  ? (String(m.content).startsWith('Authoring failed:')
                    ? m.content
                    : 'Draft updated. Review the SKILL.md on the right.')
                  : m.content}
              </div>
            ))}
            {drafting && (
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <Loader2 className="w-4 h-4 animate-spin" /> Drafting skill…
              </div>
            )}
          </div>
          <div className="border-t border-gray-200 p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={importInputRef}
                type="file"
                className="hidden"
                disabled={drafting}
                accept=".md,.zip,text/markdown,application/zip"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) await importSkillFile(file);
                }}
              />
              <button
                type="button"
                disabled={drafting}
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1.5"
                onClick={() => importInputRef.current?.click()}
              >
                <FileUp className="w-3.5 h-3.5" />
                Import skill
              </button>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Upload context
                <input
                  type="file"
                  className="hidden"
                  disabled={drafting}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.md,.txt,.png,.jpg,.jpeg"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    try {
                      await tasksApi.uploadSkillContext(skillId, file);
                      await load();
                      toast.success(`Uploaded ${file.name}`);
                    } catch (err: any) {
                      toast.error(err?.response?.data?.error || 'Upload failed');
                    }
                  }}
                />
              </label>
            </div>
            <div className="flex gap-2">
              <textarea
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                rows={3}
                placeholder="Describe the skill, required uploads, and how output should look…"
                disabled={drafting}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button
                disabled={drafting || !input.trim()}
                onClick={send}
                className="self-end px-3 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="min-h-0 overflow-y-auto p-4 bg-gray-50">
          {previewVersion ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex-1">
                  SKILL.md · version {previewVersion.version}
                </h3>
                <button
                  type="button"
                  className="text-xs text-gray-700 hover:underline"
                  onClick={() => setPreviewVersion(null)}
                >
                  Back to draft
                </button>
                <button
                  type="button"
                  disabled={drafting}
                  className="text-xs text-blue-700 hover:underline disabled:opacity-40"
                  onClick={() => restoreVersion(previewVersion)}
                >
                  Restore
                </button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-mono text-gray-800 bg-white border border-gray-200 rounded-lg p-3 min-h-[12rem]">
                {previewVersion.skillMd || 'This version has no SKILL.md.'}
              </pre>
            </>
          ) : (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">SKILL.md draft</h3>
              <pre className="text-xs whitespace-pre-wrap font-mono text-gray-800 bg-white border border-gray-200 rounded-lg p-3 min-h-[12rem]">
                {authoring?.draftSkillMd || 'Import a SKILL.md or zip, or send a message and the model will write a draft here.'}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const NameModal: React.FC<{
  title: string;
  placeholder: string;
  extraPlaceholder?: string;
  initialName?: string;
  initialExtra?: string;
  confirmLabel?: string;
  onClose: () => void;
  onSave: (name: string, extra?: string) => Promise<void>;
}> = ({ title, placeholder, extraPlaceholder, initialName = '', initialExtra = '', confirmLabel = 'Create', onClose, onSave }) => {
  const [name, setName] = useState(initialName);
  const [extra, setExtra] = useState(initialExtra);
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-96" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>
        <input className="w-full border rounded-lg px-3 py-2 text-sm mb-2" placeholder={placeholder} value={name} onChange={(e) => setName(e.target.value)} />
        {extraPlaceholder && (
          <input className="w-full border rounded-lg px-3 py-2 text-sm mb-2" placeholder={extraPlaceholder} value={extra} onChange={(e) => setExtra(e.target.value)} />
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button
            disabled={!name.trim() || saving}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-40"
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(name.trim(), extra.trim() || undefined);
              } catch (err: any) {
                toast.error(err?.response?.data?.error || 'Save failed');
              } finally {
                setSaving(false);
              }
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const MembersModal: React.FC<{ projectId: number; isAdmin: boolean; currentUserId?: number; onClose: () => void }> = ({ projectId, isAdmin, currentUserId, onClose }) => {
  const [members, setMembers] = useState<any[]>([]);
  const [available, setAvailable] = useState<{ id: number; name: string; email: string }[]>([]);
  const [selected, setSelected] = useState<number | ''>('');
  const [busy, setBusy] = useState<number | 'add' | null>(null);
  const load = async () => {
    const [m, a] = await Promise.all([tasksApi.members(projectId), tasksApi.availableCollaborators(projectId)]);
    setMembers(m.data.data || []);
    setAvailable(a.data.data || []);
  };
  useEffect(() => {
    load().catch(() => toast.error('Could not load members'));
  }, [projectId]);
  const remove = async (userId: number, userName: string) => {
    if (!window.confirm(`Remove ${userName} from this project?`)) return;
    setBusy(userId);
    try {
      await tasksApi.removeMember(projectId, userId);
      await load();
      toast.success(`${userName} removed`);
    } catch {
      toast.error('Could not remove member');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-[420px]" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 mb-3">Project members</h3>
        <ul className="text-sm space-y-1 mb-4 max-h-40 overflow-y-auto">
          {members.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 group">
              <span className="truncate">
                {m.userName} <span className="text-gray-500">{m.userEmail}</span>
              </span>
              {isAdmin && (
                <button
                  title={m.userId === currentUserId ? 'You cannot remove yourself' : `Remove ${m.userName}`}
                  disabled={busy !== null || m.userId === currentUserId}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-gray-400"
                  onClick={() => remove(m.userId, m.userName)}
                >
                  {busy === m.userId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                </button>
              )}
            </li>
          ))}
          {members.length === 0 && <li className="text-gray-500">No members yet</li>}
        </ul>
        {isAdmin ? (
          <>
            <select className="w-full border rounded-lg px-2 py-2 text-sm mb-3" value={selected} onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Add a Jarvis user…</option>
              {available.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm">Close</button>
              <button
                disabled={!selected || busy !== null}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-40"
                onClick={async () => {
                  setBusy('add');
                  try {
                    await tasksApi.addMember(projectId, Number(selected));
                    setSelected('');
                    await load();
                  } catch {
                    toast.error('Could not add member');
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                {busy === 'add' ? 'Adding…' : 'Add'}
              </button>
            </div>
          </>
        ) : (
          <div className="flex justify-end">
            <button onClick={onClose} className="px-3 py-1.5 text-sm">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksPage;
